import { supabase } from '../utils/supabase';
import { AuthResponse, AuthError, Session } from '@supabase/supabase-js';
import { isSignupEnabled } from '../config/config';

export interface SignupOptions {
  name: string;
  email: string;
  password: string;
  organizationData?: {
    organizationName: string;
    organizationSlogan?: string;
    companySize: string;
  };
}

export interface AuthServiceResponse<T = any> {
  data: T;
  error: AuthError | null;
}

export class AuthService {
  /**
   * Sign in with email and password
   */
  static async signInWithPassword(email: string, password: string): Promise<AuthResponse> {
    return await supabase.auth.signInWithPassword({
      email,
      password
    });
  }

  /**
   * Sign up a new user with organization data in metadata
   */
  static async signUp(options: SignupOptions): Promise<AuthResponse> {
    if (!isSignupEnabled()) {
      return {
        data: { user: null, session: null },
        error: { message: 'Signup is currently disabled' } as any
      };
    }

    const { name, email, password, organizationData } = options;

    return await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          needs_password: false,
          ...(organizationData && {
            pending_organization: organizationData
          })
        }
      }
    });
  }

  /**
   * Sign out the current user
   */
  static async signOut(): Promise<{ error: AuthError | null }> {
    return await supabase.auth.signOut();
  }

  /**
   * Send password reset email
   */
  static async resetPasswordForEmail(
    email: string,
    redirectTo?: string
  ): Promise<{ data: {} | null; error: AuthError | null }> {
    const options = redirectTo ? { redirectTo } : undefined;
    return await supabase.auth.resetPasswordForEmail(email, options);
  }

  /**
   * Resend verification email
   */
  static async resendVerificationEmail(
    email: string
  ): Promise<{ data: {} | null; error: AuthError | null }> {
    return await supabase.auth.resend({
      type: 'signup',
      email
    });
  }

  /**
   * Update user password
   */
  static async updatePassword(newPassword: string) {
    return await supabase.auth.updateUser({ password: newPassword });
  }

  /**
   * Update user metadata
   */
  static async updateUserMetadata(data: Record<string, any>) {
    return await supabase.auth.updateUser({ data });
  }

  /**
   * Clear user metadata completely
   */
  static async clearUserMetadata() {
    return await supabase.auth.updateUser({ data: {} });
  }

  /**
   * Remove specific metadata fields
   */
  static async removeMetadataFields(fieldsToRemove: string[]) {
    const {
      data: { user }
    } = await supabase.auth.getUser();

    if (!user) {
      return { data: null, error: { message: 'No user found' } as AuthError };
    }

    const cleanMetadata = { ...user.user_metadata };
    fieldsToRemove.forEach(field => {
      delete cleanMetadata[field];
    });

    return await supabase.auth.updateUser({ data: cleanMetadata });
  }

  /**
   * Get current session
   */
  static async getSession() {
    return await supabase.auth.getSession();
  }

  /**
   * Get current user
   */
  static async getUser() {
    return await supabase.auth.getUser();
  }

  /**
   * Set up auth state change listener
   */
  static onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    return supabase.auth.onAuthStateChange(callback);
  }

  /**
   * Create password for user (password reset completion)
   */
  static async createPassword(
    name: string,
    newPassword: string
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      // 1. Update password in auth
      const { error: passwordError } = await AuthService.updatePassword(newPassword);
      if (passwordError) {
        return { success: false, error: passwordError.message };
      }

      // 2. Update metadata (only remove needs_password flag)
      const { error: metaError } = await AuthService.updateUserMetadata({
        needs_password: false,
        name
      });
      if (metaError) {
        return { success: false, error: metaError.message };
      }

      // Note: User name update would need to be handled by UserService
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
}
